/**
 * Weekly overtime after 40 hours (Sunday–Saturday workweek).
 * California sites (customer code prefix "ca-"): daily OT after 8 hours.
 * Uses integer hundredths-of-hours (mirrors backend Decimal 2 dp ROUND_HALF_UP).
 * See docs/OVERTIME.md and docs/HOURS_DECIMAL.md.
 */

import { hoursToHundredths, hundredthsToHours } from "@/lib/hoursMath";

export const WEEKLY_OT_THRESHOLD = 40;
export const DAILY_OT_THRESHOLD = 8;
const WEEKLY_OT_CENTS = 4000;
const DAILY_OT_CENTS = 800;
export const CA_CUSTOMER_ID_PREFIX = "ca-";

/** True when business customer id starts with "ca-" (case-insensitive; match backend). */
export function isCaliforniaCustomerId(customerId: string | null | undefined): boolean {
  return Boolean(customerId && String(customerId).toLowerCase().startsWith(CA_CUSTOMER_ID_PREFIX));
}

/** Sunday on or before the given YYYY-MM-DD (local calendar date string). */
export function workweekStart(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  // JS: Sunday=0 … Saturday=6
  const day = dt.getDay();
  dt.setDate(dt.getDate() - day);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Split daily hour totals into regular vs OT using weekly 40h (Sun–Sat).
 * @param dayHours map of YYYY-MM-DD -> hours
 * @returns map of YYYY-MM-DD -> { reg, ot } (display hours, 2 dp)
 */
export function splitWeeklyOt(
  dayHours: Record<string, number>
): Record<string, { reg: number; ot: number }> {
  const dates = Object.keys(dayHours);
  if (dates.length === 0) return {};

  const byWeek: Record<string, string[]> = {};
  for (const d of dates) {
    const ws = workweekStart(d);
    if (!byWeek[ws]) byWeek[ws] = [];
    byWeek[ws].push(d);
  }

  const result: Record<string, { reg: number; ot: number }> = {};
  for (const days of Object.values(byWeek)) {
    days.sort();
    let cum = 0; // hundredths
    for (const d of days) {
      const h = hoursToHundredths(dayHours[d] || 0);
      if (h <= 0) {
        result[d] = { reg: 0, ot: 0 };
        continue;
      }
      let regC: number;
      let otC: number;
      if (cum >= WEEKLY_OT_CENTS) {
        regC = 0;
        otC = h;
      } else if (cum + h <= WEEKLY_OT_CENTS) {
        regC = h;
        otC = 0;
      } else {
        regC = WEEKLY_OT_CENTS - cum;
        otC = h - regC;
      }
      cum += h;
      result[d] = { reg: hundredthsToHours(regC), ot: hundredthsToHours(otC) };
    }
  }
  return result;
}

/** California daily OT after 8 hours (each day independent). */
export function splitDailyOt8(
  dayHours: Record<string, number>
): Record<string, { reg: number; ot: number }> {
  const result: Record<string, { reg: number; ot: number }> = {};
  for (const [d, hours] of Object.entries(dayHours)) {
    const h = hoursToHundredths(hours || 0);
    if (h <= 0) {
      result[d] = { reg: 0, ot: 0 };
      continue;
    }
    const regC = Math.min(h, DAILY_OT_CENTS);
    const otC = Math.max(0, h - DAILY_OT_CENTS);
    result[d] = { reg: hundredthsToHours(regC), ot: hundredthsToHours(otC) };
  }
  return result;
}

/**
 * CA hours → daily 8h; non-CA hours → weekly 40h; sum per day.
 * Mirrors backend split_ot_ca_and_non_ca.
 */
export function splitOtCaAndNonCa(
  caDayHours: Record<string, number>,
  nonCaDayHours: Record<string, number>
): Record<string, { reg: number; ot: number }> {
  const result: Record<string, { reg: number; ot: number }> = {};
  for (const [d, { reg, ot }] of Object.entries(splitDailyOt8(caDayHours))) {
    result[d] = { reg, ot };
  }
  for (const [d, { reg, ot }] of Object.entries(splitWeeklyOt(nonCaDayHours))) {
    const prev = result[d] || { reg: 0, ot: 0 };
    result[d] = {
      reg: hundredthsToHours(hoursToHundredths(prev.reg) + hoursToHundredths(reg)),
      ot: hundredthsToHours(hoursToHundredths(prev.ot) + hoursToHundredths(ot)),
    };
  }
  return result;
}

/** Sum reg/ot across a dayHours map (e.g. full pay period with two workweeks). */
export function sumWeeklyOtTotals(dayHours: Record<string, number>): {
  regular: number;
  ot1: number;
  ot2: number;
} {
  const split = splitWeeklyOt(dayHours);
  let regC = 0;
  let otC = 0;
  for (const { reg, ot } of Object.values(split)) {
    regC += hoursToHundredths(reg);
    otC += hoursToHundredths(ot);
  }
  return {
    regular: hundredthsToHours(regC),
    ot1: hundredthsToHours(otC),
    ot2: 0,
  };
}

/** Sum reg/ot with CA daily + non-CA weekly rules (integer hundredths). */
export function sumOtTotalsCaAndNonCa(
  caDayHours: Record<string, number>,
  nonCaDayHours: Record<string, number>
): {
  regular: number;
  ot1: number;
  ot2: number;
} {
  const split = splitOtCaAndNonCa(caDayHours, nonCaDayHours);
  let regC = 0;
  let otC = 0;
  for (const { reg, ot } of Object.values(split)) {
    regC += hoursToHundredths(reg);
    otC += hoursToHundredths(ot);
  }
  return {
    regular: hundredthsToHours(regC),
    ot1: hundredthsToHours(otC),
    ot2: 0,
  };
}
